import { useEffect, useState, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { Package, Plus } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { listConferimenti } from '../../api/conferimenti';
import type { ConferimentoListItem } from '../../api/conferimenti';
import ProgrammaConferimentoModal from '../modals/ProgrammaConferimentoModal';
import { useAuthStore } from '../../store/authStore';
import { canWriteProgrammazione } from '../../lib/constants';

interface Props {
  ordine: { id: string; status: string };
  refreshSignal?: number;
  onUpdated?: () => void;
}

const STATUS_COLOR: Record<string, string> = {
  programmato: 'bg-blue-100 text-blue-700',
  in_ritardo: 'bg-red-100 text-red-700',
  in_transito: 'bg-cyan-100 text-cyan-700',
  attesa_compliance: 'bg-amber-100 text-amber-700',
  in_stoccaggio: 'bg-green-100 text-green-700',
  respinto: 'bg-gray-100 text-gray-500',
};

// Backend conferimenti.py accetta order con status in {confermato, in_progress}.
// I vecchi stati (omologa_produttore_attesa/ricevuta, ordine_concluso/fatturato)
// non esistono più in order_status_v2 (V25). Anticipiamo la card già da `confermato`
// per dare visibilità al programmatore prima dell'avvio lavorazione.
const CONFERIMENTI_STATUSES = new Set(['confermato', 'in_progress']);

export default function ConferimentiCard({ ordine, refreshSignal, onUpdated }: Props) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { user } = useAuthStore();
  const canWrite = canWriteProgrammazione(user?.role);

  const [items, setItems] = useState<ConferimentoListItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);

  // Rules of Hooks: TUTTI gli hook devono essere chiamati prima di qualsiasi
  // early return condizionale. In una versione precedente questi due hook
  // stavano DOPO l'early return su `ordine.status` ‚Üí quando lo stato cambiava
  // da fuori-set (es. inviato_cliente) a dentro-set (es. confermato) tra
  // render, il conteggio degli hook saltava da 5 a 7 e React emetteva
  // l'errore #310 ("Rendered more hooks than during the previous render"),
  // mascherato in produzione minificata come "Unexpected Application Error".
  const enabled = CONFERIMENTI_STATUSES.has(ordine.status);

  const load = useCallback(() => {
    void (async () => {
      if (!enabled) return;
      setLoading(true);
      try {
        const res = await listConferimenti({ order_id: ordine.id, limit: 50 });
        setItems(res.items);
      } catch {
        // silently fail
      } finally {
        setLoading(false);
      }
    })();
  }, [enabled, ordine.id]);

  useEffect(() => {
    if (enabled) load();
  }, [load, refreshSignal, enabled]);

  if (!enabled) return null;

  const totaleProgrammato = items
    .filter(c => ['programmato', 'in_ritardo'].includes(c.status_display))
    .reduce((acc, c) => acc + (c.peso_stimato_kg ?? 0), 0);

  // Ricevuto = materiale entrato in stoccaggio (peso netto, fallback lordo/stimato).
  const totaleRicevuto = items
    .filter(c => c.status_display === 'in_stoccaggio')
    .reduce((acc, c) => acc + (c.peso_netto_kg ?? c.peso_lordo_kg ?? c.peso_stimato_kg ?? 0), 0);

  const totale = totaleProgrammato + totaleRicevuto;

  return (
    <>
      <div className="bg-white border border-stone-mid rounded-xl overflow-hidden">
        <div className="flex items-center justify-between px-4 py-3 border-b border-stone-mid">
          <h3 className="text-sm font-semibold text-ink-dark flex items-center gap-1.5">
            <Package size={14} className="text-sage" />
            {t('conferimenti.card.titolo')}
          </h3>
          {canWrite && (
            <button
              type="button"
              onClick={() => setShowModal(true)}
              className="flex items-center gap-1 text-xs font-medium text-sage hover:text-sage-dark transition-colors"
            >
              <Plus size={12} /> {t('conferimenti.card.programma')}
            </button>
          )}
        </div>

        <div className="divide-y divide-stone-mid">
          {loading ? (
            <div className="px-4 py-3 text-xs text-ink-soft">{t('conferimenti.card.caricamento')}</div>
          ) : items.length === 0 ? (
            <div className="px-4 py-4 text-xs text-ink-faint text-center">{t('conferimenti.card.vuoto')}</div>
          ) : (
            items.map(c => (
              <button
                key={c.id}
                type="button"
                onClick={() => navigate(`/conferimenti/${c.id}`)}
                className="w-full text-left px-4 py-2.5 hover:bg-stone-bg transition-colors"
              >
                <div className="flex items-center justify-between">
                  <span className="font-mono text-xs font-semibold text-ink-dark">{c.numero ?? '—'}</span>
                  <span className={`text-[10px] px-1.5 py-0.5 rounded font-medium ${STATUS_COLOR[c.status_display] ?? 'bg-gray-100 text-gray-600'}`}>
                    {t(`conferimenti.card.stato.${c.status_display}`, { defaultValue: c.status_display })}
                  </span>
                </div>
                <div className="text-xs text-ink-soft mt-0.5">
                  {c.data_prevista ? new Date(c.data_prevista + 'T00:00:00').toLocaleDateString('it-IT') : '—'}
                  {c.peso_stimato_kg && ` · ${(c.peso_stimato_kg / 1000).toFixed(1)} t`}
                  {c.area_tentativa_codice && ` · ${t('conferimenti.card.area', { code: c.area_tentativa_codice })}`}
                </div>
              </button>
            ))
          )}
        </div>

        {items.length > 0 && (
          <div className="px-4 py-2.5 bg-stone-bg border-t border-stone-mid">
            <div className="flex justify-between text-xs text-ink-soft">
              <span>{t('conferimenti.card.programmato')}: <span className="font-medium text-ink-dark">{(totaleProgrammato / 1000).toFixed(1)} t</span></span>
              <span>{t('conferimenti.card.ricevuto')}: <span className="font-medium text-ink-dark">{(totaleRicevuto / 1000).toFixed(1)} t</span></span>
              <span>{t('conferimenti.card.totale')}: <span className="font-medium text-ink-dark">{(totale / 1000).toFixed(1)} t</span></span>
            </div>
          </div>
        )}
      </div>

      {showModal && (
        <ProgrammaConferimentoModal
          initialOrderId={ordine.id}
          onClose={() => setShowModal(false)}
          onSaved={() => {
            setShowModal(false);
            load();
            onUpdated?.();
          }}
        />
      )}
    </>
  );
}
